import {
  AddressModel,
  JobDetailModel,
  JobSearchResultModel,
  LocationModel,
  LocationSearchResultModel,
  PlaceModel,
} from '@fleet/model';

export const centerOfAustralia = {
  latitude: '-25.2744',
  longitude: '133.7751',
};

export function locationModelFromLocationSearchResultModel(
  locationSearch: LocationSearchResultModel
) {
  return {
    locationId: locationSearch.locationId,
    organisationLocationId: locationSearch.organisationLocationId,
    organisationGroupLocationId: locationSearch.organisationGroupLocationId,
    latitude: locationSearch.latitude,
    longitude: locationSearch.longitude,
    displayLine1: locationSearch.address,
    displayLine2: '',
    name: locationSearch.name,
    type: locationSearch.locationType,
  } as LocationModel;
}

export function structuredAddressFromJobPickup(
  job: JobSearchResultModel
): AddressModel {
  return {
    placeName: job.startPlaceName,
    unitNumber: job.startUnitNumber,
    streetNumber: job.startStreetNumber,
    streetName: job.startStreetName,
    locality: job.startLocality,
    regionCode: job.startRegionCode,
    postalCode: job.startPostcode,
    countryCode: job.startCountryCode,
  };
}

export function structuredAddressFromJobDropoff(
  job: JobSearchResultModel
): AddressModel {
  return {
    placeName: job.endPlaceName,
    unitNumber: job.endUnitNumber,
    streetNumber: job.endStreetNumber,
    streetName: job.endStreetName,
    locality: job.endLocality,
    regionCode: job.endRegionCode,
    postalCode: job.endPostcode,
    countryCode: job.endCountryCode,
  };
}

export function placeModelToLocationModel(place: PlaceModel) {
  if (place) {
    const structuredAddress = place.structuredAddress
      ? {
          placeName: place.structuredAddress.placeName || null,
          unitNumber: place.structuredAddress.unitNumber || null,
          streetNumber: place.structuredAddress.streetNumber || null,
          streetName: place.structuredAddress.streetName || null,
          locality: place.structuredAddress.locality || null,
          regionCode: place.structuredAddress.regionCode || null,
          postalCode: place.structuredAddress.postalCode || null,
          countryCode: place.structuredAddress.countryCode || null,
        }
      : null;

    const location = {
      latitude: place.latitude,
      longitude: place.longitude,
      structuredAddress: structuredAddress,
      displayLine1: place.displayLine1,
      displayLine2: place.displayLine2,
    } as LocationModel;

    if (place.networkGroupLocationId) {
      location.networkGroupLocationId = place.networkGroupLocationId;
    }
    if (place.organisationLocationId) {
      location.organisationLocationId = place.organisationLocationId;
    }
    if (place.organisationGroupLocationId) {
      location.organisationGroupLocationId = place.organisationGroupLocationId;
    }
    if (place.organisationStaffLocationId) {
      location.organisationStaffLocationId = place.organisationStaffLocationId;
    }
    if (place.networkLocationId) {
      location.networkLocationId = place.networkLocationId;
    }

    return location;
  } else {
    return null;
  }
}
export function locationModelToPlaceModel(location: LocationModel) {
  return {
    latitude: location.latitude,
    longitude: location.longitude,
    structuredAddress: {
      placeName: location.structuredAddress?.placeName || null,
      unitNumber: location.structuredAddress?.unitNumber || null,
      streetNumber: location.structuredAddress?.streetNumber || null,
      streetName: location.structuredAddress?.streetName || null,
      locality: location.structuredAddress?.locality || null,
      regionCode: location.structuredAddress?.regionCode || null,
      postalCode: location.structuredAddress?.postalCode || null,
      countryCode: location.structuredAddress?.countryCode || null,
    },
    displayLine1: location.displayLine1 || null,
    displayLine2: location.displayLine2 || null,
  } as PlaceModel;
}

export function latLngStringFromLocation(location: LocationModel) {
  return location.latitude + ',' + location.longitude;
}

export function placeModelArrayFromJobDetail(jobDetail: JobDetailModel) {
  let waypoints = [jobDetail.startLocation];
  if (jobDetail.waypoints) {
    waypoints = [...waypoints, ...jobDetail.waypoints];
  }
  waypoints = [...waypoints, jobDetail.endLocation];
  return waypoints;
  // return waypoints.map((location: LocationModel) =>
  //   location ? locationModelToPlaceModel(location) : null
  // );
}

export function locationsArrayFromJobDetail(jobDetail: JobDetailModel) {
  let waypoints = [jobDetail.startLocation];
  if (jobDetail.waypoints) {
    waypoints = [...waypoints, ...jobDetail.waypoints];
  }
  waypoints = [...waypoints, jobDetail.endLocation];
  return waypoints;
}

export function jobLocationsFromLocationArray(locations: LocationModel[]) {
  let jobLocations: any;
  if (locations.length === 2) {
    jobLocations = {
      startLocation: locations[0],
      endLocation: locations[1],
      waypoints: [],
    };
  } else {
    jobLocations = {
      startLocation: locations[0],
      endLocation: locations[locations.length - 1],
      waypoints: locations.slice(1, length - 2).map((l) => {
        return { ...l, type: 'VIA' };
      }),
    };
  }
  return jobLocations;
}

export function jobLocationsFromPlaceModelArray(places: PlaceModel[]) {
  const startLocation = placeModelToLocationModel(places[0]);
  let jobLocations: any;
  if (places.length === 2) {
    jobLocations = {
      startLocation: startLocation,
      endLocation: placeModelToLocationModel(places[1]),
      waypoints: [],
    };
  } else {
    jobLocations = {
      startLocation: startLocation,
      endLocation: placeModelToLocationModel(places[places.length - 1]),
      waypoints: places.slice(1, length - 2).map((p) => {
        return { ...placeModelToLocationModel(p), type: 'VIA' };
      }),
    };
  }
  return jobLocations;
}

const alphabet = [
  'a',
  'b',
  'c',
  'd',
  'e',
  'f',
  'g',
  'h',
  'i',
  'j',
  'k',
  'l',
  'm',
  'n',
  'o',
  'p',
  'q',
  'r',
  's',
  't',
  'u',
  'v',
  'w',
  'x',
  'y',
  'z',
];

export function waypointLetterFromAlphabet(index: number): string {
  if (index || index === 0) {
    return alphabet[index].toUpperCase();
  }
  return null;
}

export function displayLinesFromAddress(
  address: AddressModel,
  onlyLocalityForDisplayLine2?: boolean
) {
  const displayLine1 = [
    address.unitNumber,
    address.streetNumber,
    address.streetName,
  ]
    .filter(Boolean)
    .join(' ');

  const displayLine2 = onlyLocalityForDisplayLine2
    ? address.locality
    : [address.locality, address.postalCode, address.regionCode]
        .filter(Boolean)
        .join(' ');
  // const displayLine2 = onlyLocalityForDisplayLine2
  //   ? address.locality
  //   : address.locality + ' ' + address.postalCode + ' ' + address.regionCode;
  return { displayLine1: displayLine1, displayLine2: displayLine2 };
}

export function displayLinesFromAddressWithPlaceName(address: AddressModel): {
  displayLine1: string;
  displayLine2: string;
} {
  const result = { displayLine1: '', displayLine2: '' };

  // Place name goes to displayLine1 if present
  if (address.placeName && address.placeName !== address.locality) {
    result.displayLine1 = address.placeName;

    // Build displayLine2 with street info and locality
    let streetInfo = '';
    if (address.streetName) {
      streetInfo =
        (address.streetNumber ? address.streetNumber + ' ' : '') +
        address.streetName;
      if (address.unitNumber) {
        streetInfo += ', #' + address.unitNumber;
      }
    }

    if (streetInfo && address.locality) {
      result.displayLine2 = streetInfo + ' ' + address.locality;
    } else if (streetInfo) {
      result.displayLine2 = streetInfo;
    } else if (address.locality) {
      result.displayLine2 = address.locality;
    }
  } else {
    // No place name - street details go to line1, locality to line2
    let streetInfo = '';
    if (address.streetName) {
      streetInfo =
        (address.streetNumber ? address.streetNumber + ' ' : '') +
        address.streetName;
      if (address.unitNumber) {
        streetInfo += ', #' + address.unitNumber;
      }
    }

    if (streetInfo) {
      result.displayLine1 = streetInfo;
    }
    if (address.locality) {
      result.displayLine2 = address.locality;
    }
  }

  return result;
}

export function displayLinesFromJobSearchResult(
  job: any,
  type: 'START' | 'END'
): { displayLine1: string; displayLine2: string } {
  const result = { displayLine1: '', displayLine2: '' };

  if (type === 'END') {
    if (job.endLocality === 'Unknown') {
      return { displayLine1: 'As Directed', displayLine2: '' };
    } else {
      // Place name goes to displayLine1 if present
      if (job.endPlaceName && job.endPlaceName !== job.endLocality) {
        result.displayLine1 = job.endPlaceName;

        // Build displayLine2 with street info and locality
        let streetInfo = '';
        if (job.endStreetName) {
          streetInfo =
            (job.endStreetNumber ? job.endStreetNumber + ' ' : '') +
            job.endStreetName;
          if (job.endUnitNumber) {
            streetInfo += ', #' + job.endUnitNumber;
          }
        }

        if (streetInfo && job.endLocality) {
          result.displayLine2 = streetInfo + ' ' + job.endLocality;
        } else if (streetInfo) {
          result.displayLine2 = streetInfo;
        } else if (job.endLocality) {
          result.displayLine2 = job.endLocality;
        }
      } else {
        // No place name - street details go to line1, locality to line2
        let streetInfo = '';
        if (job.endStreetName) {
          streetInfo =
            (job.endStreetNumber ? job.endStreetNumber + ' ' : '') +
            job.endStreetName;
          if (job.endUnitNumber) {
            streetInfo += ', #' + job.endUnitNumber;
          }
        }

        if (streetInfo) {
          result.displayLine1 = streetInfo;
        }
        if (job.endLocality) {
          result.displayLine2 = job.endLocality;
        }
      }
    }
  } else {
    // START
    // Place name goes to displayLine1 if present
    if (job.startPlaceName) {
      result.displayLine1 = job.startPlaceName;

      // Build displayLine2 with street info and locality
      let streetInfo = '';
      if (job.startStreetName) {
        streetInfo =
          (job.startStreetNumber ? job.startStreetNumber + ' ' : '') +
          job.startStreetName;
        if (job.startUnitNumber) {
          streetInfo += ', #' + job.startUnitNumber;
        }
      }

      if (
        streetInfo &&
        job.startLocality &&
        job.startLocality !== job.startPlaceName
      ) {
        result.displayLine2 = streetInfo + ' ' + job.startLocality;
      } else if (streetInfo) {
        result.displayLine2 = streetInfo;
      } else if (
        job.startLocality &&
        job.startLocality !== job.startPlaceName
      ) {
        result.displayLine2 = job.startLocality;
      }
    } else {
      // No place name - street details go to line1, locality to line2
      let streetInfo = '';
      if (job.startStreetName) {
        streetInfo =
          (job.startStreetNumber ? job.startStreetNumber + ' ' : '') +
          job.startStreetName;
        if (job.startUnitNumber) {
          streetInfo += ', #' + job.startUnitNumber;
        }
      }

      if (streetInfo) {
        result.displayLine1 = streetInfo;
      }
      if (job.startLocality) {
        result.displayLine2 = job.startLocality;
      }
    }
  }

  return result;
}
